/* CancellationReasonsWidget — analytical view of why doctors cancel cases.
 *
 * Powers the new "Cancellation reasons" panel on /admin/cases. Reads from
 * GET /api/admin/cases/cancellation-reasons?days=N where N defaults to 90.
 *
 * The point of this widget is to turn 7+ free-text cancellation strings
 * into a board-meeting-ready visualisation that surfaces conversion-killers:
 *   - "Financial constraint" leading? → revisit pricing tier mix
 *   - "Patient unreachable" high?     → tighten follow-up cadence
 *   - "Clinical reason" spiking?      → planner triage failing earlier
 *
 * Three states:
 *   - Loading skeleton
 *   - Empty state ("No cancellations in last X days — strong retention")
 *   - Stacked bar + legend grid (the main view)
 *
 * The window selector (30 / 90 / 180 days) is the only interaction —
 * everything else is read-only.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ChartBar, XCircle, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/* Bucket key → Tailwind color classes. Order in the API response feeds
   directly into the stacked bar; the colors below ensure adjacent
   segments are visually distinct. Faded slate for `unstructured` so
   legacy data visually recedes. */
const BUCKET_TONES = {
    financial:           { bar: "bg-amber-500",   dot: "bg-amber-500" },
    patient_declined:    { bar: "bg-blue-500",    dot: "bg-blue-500" },
    switched_plan:       { bar: "bg-violet-500",  dot: "bg-violet-500" },
    clinical_reason:     { bar: "bg-rose-500",    dot: "bg-rose-500" },
    patient_unreachable: { bar: "bg-orange-500",  dot: "bg-orange-500" },
    duplicate_case:      { bar: "bg-zinc-500",    dot: "bg-zinc-500" },
    other:               { bar: "bg-fuchsia-500", dot: "bg-fuchsia-500" },
    unstructured:        { bar: "bg-slate-300",   dot: "bg-slate-300" },
};

const WINDOW_OPTIONS = [
    { days: 30,  label: "30d" },
    { days: 90,  label: "90d" },
    { days: 180, label: "180d" },
];

function timeAgo(iso) {
    if (!iso) return "";
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `${mins}m ago`;
    if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
    return `${Math.floor(mins / 60 / 24)}d ago`;
}

export default function CancellationReasonsWidget() {
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.get(`/admin/cases/cancellation-reasons?days=${days}`)
            .then((r) => {
                if (!cancelled) setData(r.data);
            })
            .catch((e) => {
                if (!cancelled) setError(e.response?.data?.detail || "Couldn't load cancellation analytics");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [days]);

    if (loading && !data) {
        return (
            <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 animate-pulse" data-testid="cancel-reasons-loading">
                <div className="h-4 w-44 bg-secondary rounded mb-3" />
                <div className="h-2 w-full bg-secondary rounded mb-4" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-secondary rounded" />)}
                </div>
            </div>
        );
    }

    const total = data?.total || 0;
    const buckets = data?.buckets || [];
    const recent = data?.recent || [];

    return (
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid="cancel-reasons-widget">
            {/* HEADER */}
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-rose-50 grid place-items-center shrink-0">
                            <XCircle size={14} weight="fill" className="text-rose-700" />
                        </div>
                        <h2 className="font-display text-base font-bold">Cancellation reasons</h2>
                        <span className="text-[10px] tabular-nums font-bold bg-foreground text-card px-1.5 py-0.5 rounded" data-testid="cancel-reasons-total">
                            {total}
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-9">
                        Why doctors abort cases — surface conversion-killers + retention signals.
                    </p>
                </div>
                {/* Window selector */}
                <div className="flex items-center gap-1 bg-secondary/60 p-0.5 rounded-full" data-testid="cancel-reasons-window">
                    {WINDOW_OPTIONS.map((o) => (
                        <button
                            key={o.days}
                            type="button"
                            onClick={() => setDays(o.days)}
                            className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                                days === o.days
                                    ? "bg-foreground text-card"
                                    : "text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`cancel-reasons-window-${o.days}`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>

            {error ? (
                <div className="text-xs text-rose-700 px-1" data-testid="cancel-reasons-error">{error}</div>
            ) : total === 0 ? (
                <EmptyState days={days} />
            ) : (
                <>
                    {/* STACKED BAR */}
                    <div className="h-3 bg-secondary rounded-full overflow-hidden flex" data-testid="cancel-reasons-bar">
                        {buckets.map((b) => {
                            const tone = BUCKET_TONES[b.key] || BUCKET_TONES.unstructured;
                            return (
                                <div
                                    key={b.key}
                                    className={`h-full ${tone.bar}`}
                                    style={{ width: `${b.pct}%` }}
                                    title={`${b.label}: ${b.count} (${b.pct}%)`}
                                    data-testid={`cancel-reasons-bar-${b.key}`}
                                />
                            );
                        })}
                    </div>

                    {/* LEGEND GRID */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-4" data-testid="cancel-reasons-legend">
                        {buckets.map((b) => {
                            const tone = BUCKET_TONES[b.key] || BUCKET_TONES.unstructured;
                            return (
                                <div
                                    key={b.key}
                                    className="flex items-start gap-2 p-2.5 rounded-lg border border-border hover:border-border/80 transition-colors"
                                    data-testid={`cancel-reasons-row-${b.key}`}
                                >
                                    <span className={`w-2.5 h-2.5 rounded-full ${tone.dot} mt-1 shrink-0`} />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs font-semibold leading-tight">{b.label}</div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
                                            <span className="font-bold text-foreground">{b.count}</span>
                                            <span className="mx-1">·</span>
                                            <span>{b.pct}%</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* RECENT — quick drilldown list */}
                    {recent.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-border" data-testid="cancel-reasons-recent">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2">
                                Recent cancellations
                            </div>
                            <ul className="space-y-1.5">
                                {recent.map((r) => (
                                    <li key={r.id} className="flex items-start gap-2 text-xs">
                                        <Link
                                            to={`/admin/cases/${r.id}`}
                                            className="font-mono font-semibold text-foreground hover:text-primary hover:underline shrink-0"
                                        >
                                            {r.code}
                                        </Link>
                                        <span className="text-muted-foreground line-clamp-1 flex-1 min-w-0">
                                            {r.cancellation_reason || "—"}
                                        </span>
                                        <span className="text-muted-foreground/70 tabular-nums shrink-0">{timeAgo(r.cancelled_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}


function EmptyState({ days }) {
    return (
        <div className="flex items-start gap-3 py-2" data-testid="cancel-reasons-empty">
            <div className="w-10 h-10 rounded-full bg-emerald-50 grid place-items-center shrink-0">
                <CheckCircle size={18} weight="fill" className="text-emerald-600" />
            </div>
            <div>
                <div className="font-semibold text-sm">No cancellations in the last {days} days</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                    Strong retention — every started case is still in motion. <Link to="/admin/cases" className="text-primary hover:underline inline-flex items-center gap-0.5">View all cases <ArrowRight size={10} weight="bold" /></Link>
                </div>
            </div>
        </div>
    );
}
